import { useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, LogIn, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { endImpersonation, startImpersonation } from "@/lib/impersonate";
import { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Always-visible bar while a super admin is viewing as another user. */
export const ImpersonationBanner = ({ user, company }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-amber-400 px-3 py-1.5 text-slate-900 sm:px-4" role="status" data-testid="impersonation-banner">
      <Eye className="h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 text-xs leading-snug sm:text-sm">
        Viewing as <span className="font-bold" data-testid="impersonation-name">{user.name}</span> <span className="break-all">({user.email})</span>{company && <> · <span className="font-semibold">{company}</span></>}
      </div>
      <button type="button" onClick={() => { setBusy(true); endImpersonation(); }} disabled={busy}
        className="inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-full bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:ring-offset-amber-400"
        data-testid="impersonation-return">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} Return to admin</button>
    </div>
  );
};

/** "Open account" button for super admins (not for super admin or deactivated accounts). */
export const OpenAccountButton = ({ u, className, label = "Open account" }) => {
  const [busy, setBusy] = useState(false);
  if (u.is_super_admin || u.is_self || u.status !== "active") return null;
  const go = async () => { setBusy(true); try { await startImpersonation(u.id); } catch (e) { toast.error(apiError(e)); setBusy(false); } };
  return (
    <Button size="sm" variant="outline" className={cn("gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50", className)} onClick={go} disabled={busy} title="Open this user's account" data-testid={`open-account-${u.id}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}{label && <span>{label}</span>}
    </Button>
  );
};
